/**
 * Availability/Leave Edit Dialog Component
 *
 * Modal dialog for editing or deleting existing availability blocks or leave time.
 * Handles both "Available" slots and leave requests (sick, vacation, lunch).
 *
 * @module Calendar/dialogs/AvailabilityEditDialog
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';

const envVariable = require('../../../EnvironmentVariables.json');

/**
 * Availability/Leave Edit Dialog
 *
 * Allows editing times or deleting an existing availability block or leave time.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {Object} props.availability - The availability/leave block to edit
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {Function} props.onSuccess - Callback when block is successfully updated/deleted
 * @returns {React.Element} Edit dialog
 */
export const AvailabilityEditDialog = ({ open, availability, onClose, onSuccess }) => {
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [selectedDate, setSelectedDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [timeErr, setTimeErr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [spansMultipleDays, setSpansMultipleDays] = useState(false);
  const [conflictingTasks, setConflictingTasks] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'update' or 'delete'
  const [skipConflictCheck, setSkipConflictCheck] = useState(false); // Skip conflict check on re-trigger

  // Parse datetime string from backend as UTC (backend stores all timestamps in UTC)
  const parseUTCDateTime = (dateString) => {
    if (!dateString) return null;
    // Backend returns format: "YYYY-MM-DD HH:MM:SS"
    // Replace space with T and add Z to indicate UTC
    const isoString = dateString.replace(' ', 'T') + (dateString.includes('Z') ? '': 'Z');
    const date = new Date(isoString);
    return isNaN(date.getTime()) ? null : date;
  };

  // Determine if this is a leave block or availability block
  const isLeaveBlock = availability?.mech_status === 'Requested off';
  const leaveTypeLabel = {
    sick: 'Sick Time',
    vacation: 'Vacation',
    lunch: 'Lunch Break'
  }[availability?.leave_reason] || 'Leave';

  // Populate form when availability changes
  useEffect(() => {
    if (availability && open) {
      const start = new Date(availability.startDate);
      const end = new Date(availability.endDate);

      // Format dates as YYYY-MM-DD
      const startDateStr = start.toISOString().split('T')[0];
      const endDateStr = end.toISOString().split('T')[0];

      setSelectedDate(startDateStr);
      setEndDate(endDateStr);

      // Check if spans multiple days
      const spansMultiple = startDateStr !== endDateStr;
      setSpansMultipleDays(spansMultiple);

      // Format times as HH:MM
      const startTimeStr = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
      const endTimeStr = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;

      setStartTime(startTimeStr);
      setEndTime(endTimeStr);
      setFeedback(null);
      setTimeErr(false);
      setSkipConflictCheck(false);
    }
  }, [availability, open]);

  // Check for tasks that would be affected by availability or leave changes
  const checkConflictingTasks = async (newStartDateTime, newEndDateTime) => {
    const originalStart = new Date(availability.startDate);
    const originalEnd = new Date(availability.endDate);
    const newStart = new Date(newStartDateTime);
    const newEnd = new Date(newEndDateTime);

    // Format original times as UTC datetime strings for comparison with task timestamps
    const originalStartUTC = originalStart.toISOString().slice(0, 19).replace('T', ' ');
    const originalEndUTC = originalEnd.toISOString().slice(0, 19).replace('T', ' ');

    if (isLeaveBlock) {
      // For leave blocks: check if expanding (which would affect availability blocks)
      const isExpanding = newStart < originalStart || newEnd > originalEnd;

      if (!isExpanding) {
        // Not expanding leave, no conflicts
        return [];
      }

      try {
        // First, check for overlapping leave blocks
        const conflictResponse = await fetch(envVariable.serverId + '/SCO/checkBlockConflicts.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mechanic_id: availability.mechanic_id,
            sc_id: availability.sc_id,
            location_id: availability.location_id,
            start_date_time: newStartDateTime,
            end_date_time: newEndDateTime,
            block_type: 'leave',
            exclude_current: true,
            original_start_date_time: originalStartUTC,
            original_end_date_time: originalEndUTC,
          }),
        });

        const conflictResult = await conflictResponse.json();

        // Check if there are conflicting leave blocks
        if (conflictResult.leave_conflicts && conflictResult.leave_conflicts.length > 0) {
          setFeedback({
            message: 'This leave period conflicts with another leave period for this mechanic',
            success: false
          });
          setLoading(false);
          return null; // Signal to abort
        }

        // Find tasks in overlapping availability blocks
        const tasksResponse = await fetch(envVariable.serverId + '/SCO/checkAffectedTasks.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mechanic_id: availability.mechanic_id,
            sc_id: availability.sc_id,
            old_start_date_time: newStartDateTime, // The new expanded leave period
            old_end_date_time: newEndDateTime,
            new_start_date_time: originalStartUTC, // Empty window
            new_end_date_time: originalStartUTC,
          }),
        });

        const tasksResult = await tasksResponse.json();
        return tasksResult.affected_tasks || [];
      } catch (error) {
        console.error('[AvailabilityEditDialog] Error checking leave expansion conflicts:', error);
        return [];
      }
    } else {
      // For availability blocks: check if shrinking (tasks could be affected)
      const isReducing = newStart > originalStart || newEnd < originalEnd;

      if (!isReducing) {
        // Expanding availability, no conflicts
        return [];
      }

      try {
        // First, check for overlapping blocks
        const conflictResponse = await fetch(envVariable.serverId + '/SCO/checkBlockConflicts.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mechanic_id: availability.mechanic_id,
            sc_id: availability.sc_id,
            location_id: availability.location_id,
            start_date_time: newStartDateTime,
            end_date_time: newEndDateTime,
            block_type: 'availability',
            exclude_current: true,
            original_start_date_time: originalStartUTC,
            original_end_date_time: originalEndUTC,
          }),
        });

        const conflictResult = await conflictResponse.json();

        // Check for conflicts with leave blocks
        if (conflictResult.leave_conflicts && conflictResult.leave_conflicts.length > 0) {
          setFeedback({
            message: 'This availability period conflicts with a leave period for this mechanic',
            success: false
          });
          setLoading(false);
          return null; // Signal to abort
        }

        // Fetch tasks assigned to this mechanic that overlap with the REMOVED time period
        const response = await fetch(envVariable.serverId + '/SCO/checkAffectedTasks.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mechanic_id: availability.mechanic_id,
            sc_id: availability.sc_id,
            old_start_date_time: originalStartUTC,
            old_end_date_time: originalEndUTC,
            new_start_date_time: newStartDateTime,
            new_end_date_time: newEndDateTime,
          }),
        });

        const result = await response.json();
        return result.affected_tasks || [];
      } catch (error) {
        console.error('[AvailabilityEditDialog] Error checking conflicts:', error);
        return [];
      }
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeErr(false);
    setFeedback(null);

    const startTimeHour = parseInt(startTime.substring(0, 2));
    const startTimeMinute = parseInt(startTime.substring(3, 5));
    const endTimeHour = parseInt(endTime.substring(0, 2));
    const endTimeMinute = parseInt(endTime.substring(3, 5));

    // Validate time format
    if (isNaN(startTimeHour) || isNaN(startTimeMinute) || isNaN(endTimeHour) || isNaN(endTimeMinute)) {
      setTimeErr(true);
      setLoading(false);
      return;
    }

    if (
      startTime[2] !== ':' ||
      endTime[2] !== ':' ||
      startTime.length !== 5 ||
      endTime.length !== 5 ||
      startTimeHour > 23 ||
      startTimeMinute > 59 ||
      endTimeHour > 23 ||
      endTimeMinute > 59
    ) {
      setTimeErr(true);
      setLoading(false);
      return;
    }

    // For multi-day blocks, validate date range
    if (spansMultipleDays && selectedDate > endDate) {
      setFeedback({ message: 'End date must be after start date', success: false });
      setLoading(false);
      return;
    }

    // For single-day blocks, ensure start time is before end time
    if (!spansMultipleDays && (startTimeHour > endTimeHour || (startTimeHour === endTimeHour && startTimeMinute >= endTimeMinute))) {
      setTimeErr(true);
      setLoading(false);
      return;
    }

    // Use endDate for multi-day blocks, otherwise use selectedDate for both
    const effectiveEndDate = spansMultipleDays ? endDate : selectedDate;

    // Convert local times to UTC before sending to backend
    // User enters times in their local timezone, we convert to UTC for storage
    const localStartDateTime = new Date(`${selectedDate}T${startTime}:00`);
    const localEndDateTime = new Date(`${effectiveEndDate}T${endTime}:00`);

    const new_start_date_time = localStartDateTime.toISOString().slice(0, 19).replace('T', ' ');
    const new_end_date_time = localEndDateTime.toISOString().slice(0, 19).replace('T', ' ');

    // Check for conflicting tasks (for both availability and leave blocks)
    if (!skipConflictCheck) {
      const conflicts = await checkConflictingTasks(new_start_date_time, new_end_date_time);

      // null means validation failed (e.g., overlapping blocks)
      if (conflicts === null) {
        setLoading(false);
        return;
      }

      // If there are conflicts, show confirmation dialog
      if (conflicts.length > 0) {
        console.log(`[AvailabilityEditDialog] Found ${conflicts.length} conflicting tasks when updating ${isLeaveBlock ? 'leave' : 'availability'} block`);
        setConflictingTasks(conflicts);
        setShowConfirmation(true);
        setPendingAction('update');
        setLoading(false);
        return;
      }
    }

    try {
      let response;

      if (isLeaveBlock) {
        // Update leave block - use original dates from availability object
        const originalStart = new Date(availability.startDate);
        const originalEnd = new Date(availability.endDate);

        // Convert to UTC for backend (currently stored as UTC in DB after migration)
        const old_start_date_time = originalStart.toISOString().slice(0, 19).replace('T', ' ');
        const old_end_date_time = originalEnd.toISOString().slice(0, 19).replace('T', ' ');

        response = await fetch(envVariable.serverId + '/SCO/updateLeave.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mechanic_id: availability.mechanic_id,
            old_start_date_time,
            old_end_date_time,
            new_start_date_time,
            new_end_date_time,
            sc_id: availability.sc_id,
            leave_reason: availability.leave_reason,
          }),
        });
      } else {
        // Update availability block - include original times to identify which slots to delete
        const originalStart = new Date(availability.startDate);
        const originalEnd = new Date(availability.endDate);

        // Convert to UTC for backend (currently stored as UTC in DB after migration)
        const original_start_date_time = originalStart.toISOString().slice(0, 19).replace('T', ' ');
        const original_end_date_time = originalEnd.toISOString().slice(0, 19).replace('T', ' ');

        response = await fetch(envVariable.serverId + '/mechanicSlots/updateAvailability.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mechanic_id: availability.mechanic_id,
            date: availability.date,
            sc_id: availability.sc_id,
            location_id: availability.location_id,
            start_date_time: new_start_date_time,
            end_date_time: new_end_date_time,
            original_start_date_time,
            original_end_date_time,
          }),
        });
      }

      const result = await response.json();

      if (result.type === 'success') {
        setFeedback({
          message: isLeaveBlock ? 'Leave time updated successfully!' : 'Availability updated successfully!',
          success: true
        });
        // Call onSuccess after showing success message
        // Parent will handle closing the dialog and refreshing data
        setTimeout(async () => {
          if (onSuccess) {
            onSuccess(); // Triggers refresh
            // Wait for refresh to complete before allowing user interaction
            await new Promise(resolve => setTimeout(resolve, 800));
          }
          handleClose();
        }, 800);
      } else {
        setFeedback({ message: result.message || 'Failed to update', success: false });
      }
    } catch (error) {
      console.error('Error updating:', error);
      setFeedback({
        message: 'Unexpected error occurred. Please try again.',
        success: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setFeedback(null);

    const start = new Date(availability.startDate);
    const end = new Date(availability.endDate);

    // Check for conflicting tasks before deleting (for both availability and leave blocks)
    if (!skipConflictCheck) {
      // Format as UTC datetime string for backend
      const startDateTimeStr = start.toISOString().slice(0, 19).replace('T', ' ');
      const endDateTimeStr = end.toISOString().slice(0, 19).replace('T', ' ');

      // When deleting, check for tasks assigned in this time period
      // For availability: tasks will lose their availability window
      // For leave: tasks were incorrectly assigned during leave time
      try {
        const response = await fetch(envVariable.serverId + '/SCO/checkAffectedTasks.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mechanic_id: availability.mechanic_id,
            sc_id: availability.sc_id,
            old_start_date_time: startDateTimeStr,
            old_end_date_time: endDateTimeStr,
            new_start_date_time: startDateTimeStr, // Empty window after deletion
            new_end_date_time: startDateTimeStr,
          }),
        });

        const result = await response.json();
        const conflicts = result.affected_tasks || [];

        if (conflicts.length > 0) {
          console.log(`[AvailabilityEditDialog] Found ${conflicts.length} conflicting tasks when deleting ${isLeaveBlock ? 'leave' : 'availability'} block`);
          setConflictingTasks(conflicts);
          setShowConfirmation(true);
          setPendingAction('delete');
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('[AvailabilityEditDialog] Error checking conflicts for deletion:', error);
        // Continue anyway - don't block on error
      }
    }

    // If no conflicts and user hasn't already confirmed, show confirmation
    // Skip confirmation if we're re-triggering after conflict resolution
    if (!skipConflictCheck) {
      const confirmMsg = isLeaveBlock
        ? `Are you sure you want to delete this ${leaveTypeLabel.toLowerCase()}?`
        : 'Are you sure you want to delete this availability block?';

      if (!window.confirm(confirmMsg)) {
        setLoading(false);
        return;
      }
    }

    try {
      let response;

      if (isLeaveBlock) {
        // Delete leave block - convert to UTC for backend
        const start_date_time = start.toISOString().slice(0, 19).replace('T', ' ');
        const end_date_time = end.toISOString().slice(0, 19).replace('T', ' ');

        response = await fetch(envVariable.serverId + '/SCO/deleteLeave.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mechanic_id: availability.mechanic_id,
            start_date_time,
            end_date_time,
            sc_id: availability.sc_id,
          }),
        });
      } else {
        // Delete availability block - convert to UTC for backend
        const start_date_time = start.toISOString().slice(0, 19).replace('T', ' ');
        const end_date_time = end.toISOString().slice(0, 19).replace('T', ' ');

        response = await fetch(envVariable.serverId + '/mechanicSlots/deleteAvailability.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mechanic_id: availability.mechanic_id,
            sc_id: availability.sc_id,
            location_id: availability.location_id,
            start_date_time,
            end_date_time,
          }),
        });
      }

      const result = await response.json();

      if (result.type === 'success') {
        setFeedback({
          message: isLeaveBlock ? 'Leave time deleted successfully!' : 'Availability deleted successfully!',
          success: true
        });
        setTimeout(async () => {
          if (onSuccess) {
            onSuccess(); // Triggers refresh
            // Wait for refresh to complete
            await new Promise(resolve => setTimeout(resolve, 800));
          }
          handleClose();
        }, 800);
      } else {
        setFeedback({ message: result.message || 'Failed to delete', success: false });
      }
    } catch (error) {
      console.error('Error deleting:', error);
      setFeedback({
        message: 'Unexpected error occurred. Please try again.',
        success: false,
      });
    } finally {
      setLoading(false);
    }
  };

  // Unassign tasks that no longer fit in the availability
  const unassignConflictingTasks = async () => {
    if (conflictingTasks.length === 0) return true;

    const taskIds = conflictingTasks.map(t => t.task_id);
    console.log('[AvailabilityEditDialog] Unassigning tasks:', {
      count: conflictingTasks.length,
      task_ids: taskIds,
      tasks: conflictingTasks.map(t => ({ id: t.task_id, info: t.service_info }))
    });

    try {
      const response = await fetch(envVariable.serverId + '/SCO/unassignTasks.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task_ids: taskIds,
          sc_id: availability.sc_id,
        }),
      });

      const result = await response.json();
      console.log('[AvailabilityEditDialog] Unassign response:', result);

      if (result.type === 'success') {
        console.log(`[AvailabilityEditDialog] Successfully unassigned ${result.count || taskIds.length} tasks`);
      } else {
        console.error('[AvailabilityEditDialog] Unassign failed:', result.message);
      }

      return result.type === 'success';
    } catch (error) {
      console.error('[AvailabilityEditDialog] Error unassigning tasks:', error);
      return false;
    }
  };

  // User confirms they want to proceed despite conflicts
  const handleProceedWithConflicts = async () => {
    setShowConfirmation(false);
    setLoading(true);

    // Unassign the conflicting tasks first
    const unassignSuccess = await unassignConflictingTasks();
    if (!unassignSuccess) {
      setFeedback({ message: 'Failed to unassign conflicting tasks', success: false });
      setLoading(false);
      return;
    }

    console.log('[AvailabilityEditDialog] Tasks unassigned successfully');
    console.log('[AvailabilityEditDialog] Database should now show these tasks with mechanic_id=NULL:', conflictingTasks.map(t => t.task_id));

    // Extended delay to ensure database changes are fully committed and visible to subsequent queries
    // This prevents the calendar refresh from reading stale data
    console.log('[AvailabilityEditDialog] Waiting 1000ms for database commit to propagate...');
    await new Promise(resolve => setTimeout(resolve, 1000));

    console.log('[AvailabilityEditDialog] Proceeding with availability update after unassignment');

    // If this is a leave block expansion, reduce overlapping availability blocks
    if (isLeaveBlock && pendingAction === 'update') {
      const start = new Date(availability.startDate);
      const end = new Date(availability.endDate);
      const newStart = new Date(`${selectedDate} ${startTime}:00`);
      const newEnd = new Date(`${spansMultipleDays ? endDate : selectedDate} ${endTime}:00`);

      // Check if expanding
      const isExpanding = newStart < start || newEnd > end;

      if (isExpanding) {
        const effectiveEndDate = spansMultipleDays ? endDate : selectedDate;

        // Convert local times to UTC before sending to backend
        // User enters times in their local timezone, we convert to UTC for storage
        const localStartDateTime = new Date(`${selectedDate}T${startTime}:00`);
        const localEndDateTime = new Date(`${effectiveEndDate}T${endTime}:00`);

        const new_start_date_time = localStartDateTime.toISOString().slice(0, 19).replace('T', ' ');
        const new_end_date_time = localEndDateTime.toISOString().slice(0, 19).replace('T', ' ');

        try {
          const reduceResponse = await fetch(envVariable.serverId + '/SCO/reduceAvailabilityForLeave.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              mechanic_id: availability.mechanic_id,
              sc_id: availability.sc_id,
              leave_start: new_start_date_time,
              leave_end: new_end_date_time,
            }),
          });

          const reduceResult = await reduceResponse.json();

          if (reduceResult.type !== 'success') {
            setFeedback({ message: 'Failed to adjust overlapping availability', success: false });
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('[AvailabilityEditDialog] Error reducing availability for leave:', error);
          setFeedback({ message: 'Error adjusting availability blocks', success: false });
          setLoading(false);
          return;
        }
      }
    }

    // Set flag to skip conflict checking on re-trigger
    setSkipConflictCheck(true);

    // Now proceed with the original action
    if (pendingAction === 'update') {
      // Re-trigger update with confirmation bypassed
      const event = { preventDefault: () => {} };
      await handleUpdate(event);
    } else if (pendingAction === 'delete') {
      // Re-trigger delete with confirmation bypassed
      await handleDelete();
    }

    // Reset state
    setConflictingTasks([]);
    setPendingAction(null);
    setSkipConflictCheck(false);
  };

  // User cancels the operation
  const handleCancelConflicts = () => {
    setShowConfirmation(false);
    setConflictingTasks([]);
    setPendingAction(null);
    setSkipConflictCheck(false);
    setLoading(false);
  };

  const handleClose = () => {
    setFeedback(null);
    setTimeErr(false);
    setShowConfirmation(false);
    setConflictingTasks([]);
    setPendingAction(null);
    setSkipConflictCheck(false);
    onClose();
  };

  const mechanicName = availability?.mechanic_first_name && availability?.mechanic_last_name
    ? `${availability.mechanic_first_name} ${availability.mechanic_last_name}`
    : 'Unknown Mechanic';

  const dialogTitle = isLeaveBlock ? `Edit ${leaveTypeLabel}` : 'Edit Availability Block';
  const blockTypeColor = isLeaveBlock
    ? (availability.leave_reason === 'sick' ? 'error' : availability.leave_reason === 'vacation' ? 'info' : 'warning')
    : 'success';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {showConfirmation ? 'Conflicting Tasks Warning' : dialogTitle}
      </DialogTitle>

      <DialogContent>
        {showConfirmation ? (
          /* Confirmation view when there are conflicting tasks */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="warning">
              {pendingAction === 'delete'
                ? 'Deleting this availability will unassign the following tasks:'
                : 'Reducing availability will unassign the following tasks:'}
            </Alert>

            <Typography variant="body2" color="text.secondary">
              These tasks are currently assigned to <strong>{mechanicName}</strong> but would no longer fit in the available time:
            </Typography>

            <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              {conflictingTasks.map((task, idx) => (
                <ListItem key={idx}>
                  <ListItemText
                    primary={task.service_info || task.title || `Task #${task.task_id}`}
                    secondary={`${parseUTCDateTime(task.startDate)?.toLocaleString() || 'N/A'} - ${parseUTCDateTime(task.endDate)?.toLocaleString() || 'N/A'}`}
                  />
                </ListItem>
              ))}
            </List>

            <Alert severity="info">
              If you proceed, these {conflictingTasks.length} task{conflictingTasks.length > 1 ? 's' : ''} will be marked as unassigned and will need to be reassigned to another mechanic.
            </Alert>
          </Box>
        ) : (
          /* Normal edit form */
          <Box component="form" onSubmit={handleUpdate} sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {isLeaveBlock ? 'Editing leave time' : 'Editing availability'} for <strong>{mechanicName}</strong>
              </Typography>
              <Chip
                label={isLeaveBlock ? leaveTypeLabel : 'Available'}
                color={blockTypeColor}
                size="small"
              />
            </Box>

          {/* Start Date */}
          <TextField
            fullWidth
            label={spansMultipleDays ? "Start Date" : "Date"}
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={!isLeaveBlock}
            helperText={!isLeaveBlock ? "Cannot change date for availability blocks" : spansMultipleDays ? "Start date of the leave period" : ""}
          />

          {/* End Date (only shown for multi-day leave blocks) */}
          {spansMultipleDays && (
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="End date of the leave period"
            />
          )}

          {/* Start Time */}
          <TextField
            fullWidth
            label="Start Time"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          {/* End Time */}
          <TextField
            fullWidth
            label="End Time"
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />
          {/* Multi-day info */}
          {spansMultipleDays && (
            <Alert severity="info" sx={{ mt: 1 }}>
              This leave block spans multiple days. You can edit both the start and end dates.
            </Alert>
          )}

          {timeErr && (
            <Typography color="error" variant="caption">
              Invalid time. {spansMultipleDays ? "Check that times are in valid format (HH:MM)." : "Ensure start time is earlier than end time."}
            </Typography>
          )}

          {/* Feedback Message */}
          {feedback && (
            <Alert severity={feedback.success ? 'success' : 'error'}>{feedback.message}</Alert>
          )}
        </Box>
        )}
      </DialogContent>

      <DialogActions>
        {showConfirmation ? (
          /* Confirmation dialog actions */
          <>
            <Button onClick={handleCancelConflicts} variant="outlined" disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleProceedWithConflicts} variant="contained" color="warning" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Proceed & Unassign Tasks'}
            </Button>
          </>
        ) : (
          /* Normal edit dialog actions */
          <>
            <Button onClick={handleDelete} variant="outlined" color="error" disabled={loading}>
              Delete
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleUpdate} variant="contained" color="primary" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
